import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/pro-light-svg-icons/faEnvelope';
import { TypedMemo } from '../../../../common/types';
import type { OutreachLivePlanned, OutreachLiveWarmup } from '../../outreachTypes';
import { warmupDisplayDay } from '../warmupDay';
import { LiveLabel } from './LiveLabel';
import { UpNextRow } from './UpNextRow';
import { dayCount, dayWord, plannedAt } from './upNextCopy';
import { useNewRows } from './useNewRows';
import { upNextMessages as messages } from './upNextMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Up next; each part uses its own slice of it */
import styles from './UpNext.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The most rows of the card; the rest is in People. */
const MAX_ROWS = 6;

interface UpNextCardProps {
  planned: OutreachLivePlanned;
  warmup: OutreachLiveWarmup | null;
  /** The plan's pitches a day of this campaign (`campaigns.get` `dailyRate`); null while it loads. */
  rate: number | null;
  now: Date;
  onSeeAll: () => void;
}

/**
 * "Up next" (design outreach-v2 s2, s3, s4): the people that Plug picked, with their names before
 * the send (owner decision 2026-10-27), the time in their zone and in the artist's, and a live
 * state per row. While Plug writes the head says "Writing 2 of 3".
 */
const UpNextCardComponent: React.FC<UpNextCardProps> = ({ planned, warmup, rate, now, onSeeAll }) => {
  const intl = useIntl();
  const { pitches, total } = planned;
  const shown = useMemo(() => pitches.slice(0, MAX_ROWS), [pitches]);
  const fresh = useNewRows(useMemo(() => shown.map((pitch) => pitch.messageUuid), [shown]));
  // The list holds at most 20 pitches: the writing count and its total both come from it.
  const writing = pitches.filter((pitch) => pitch.state === 'writing').length;
  const sending = pitches.some((pitch) => pitch.state === 'sending');
  const first = pitches[0];
  const day = dayWord(intl, first ? plannedAt(first) : null, now);
  const cap = dayCount(rate, warmup);

  const live = () => {
    if (writing > 0) {
      return (
        <LiveLabel
          text={intl.formatMessage(messages.liveWriting, { done: pitches.length - writing, count: pitches.length })}
        />
      );
    }
    if (sending) {
      return <LiveLabel text={intl.formatMessage(messages.liveSending)} positive />;
    }
    return (
      <button type="button" className={styles.link} onClick={onSeeAll} data-testid="up-next-see-all">
        {intl.formatMessage(messages.seeAll)}
      </button>
    );
  };

  const foot = () => {
    if (writing > 0) {
      return intl.formatMessage(messages.footWriting);
    }
    if (warmup !== null && warmup.warming && rate !== null && cap !== null && cap < rate) {
      return intl.formatMessage(messages.footWarmup, {
        day: warmupDisplayDay(warmup),
        days: warmup.totalDays,
        cap,
        rate,
      });
    }
    return null;
  };
  const footText = foot();

  return (
    <section className={styles.card} data-testid="up-next">
      <div className={styles.head}>
        <h3 className={styles.title}>
          {intl.formatMessage(messages.title)}{' '}
          <span className={styles.suffix}>
            {intl.formatMessage(writing > 0 ? messages.suffixWriting : messages.suffixDay, { count: total, day })}
          </span>
        </h3>
        {live()}
      </div>
      <div className={styles.list}>
        {shown.map((pitch) => (
          <UpNextRow key={pitch.messageUuid} pitch={pitch} now={now} isNew={fresh.has(pitch.messageUuid)} />
        ))}
      </div>
      {(footText !== null || total > shown.length) && (
        <div className={styles.foot}>
          <FontAwesomeIcon icon={faEnvelope} className={styles.footIcon} />
          <span className={styles.footText}>{footText}</span>
          {total > shown.length && (
            <button type="button" className={styles.link} onClick={onSeeAll}>
              {intl.formatMessage(messages.more, { count: total - shown.length })}
            </button>
          )}
        </div>
      )}
    </section>
  );
};

export const UpNextCard = TypedMemo(UpNextCardComponent);
